import { type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import { json, Link, useLoaderData, useNavigate } from "@remix-run/react";
import { useState } from "react";
import Footer from "../components/Footer";
import Header from "../components/header";
import { getSupabaseWithSessionHeaders } from "../lib/supabase_server";
import { PUBLIC_STORE_PROFILES } from "../lib/public_store_profiles";
import {
  getSessionUser,
  redirectOnIncompleteOnBoarding,
} from "../lib/onboarding.server";
import { Product, User } from "../lib/types";
import { isHomepageSupplierStore } from "../lib/store_business_type";
import { ProductCard } from "../components/ProductCard";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed";
import StoreCard from "../components/StoreCard";
import HorizontalScroller from "../components/HorizontalScroller";
import { Globe } from "~/components/ui/globe";
import Button from "~/components/theme/Button";
import { isValidUsZipCode, sanitizeUsZipInput } from "~/lib/usZip";
import { ArrowRight, CheckCircle2, Store } from "lucide-react";

export const meta: MetaFunction = () => {
  const title = "BulkBuyPlace — International Food Wholesale Marketplace";
  const description =
    "Source international, ethnic, and specialty food products from verified wholesalers and distributors. BulkBuyPlace helps retailers, restaurants, and small businesses buy in bulk at competitive wholesale prices.";

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "BulkBuyPlace",
    url: "https://bulkbuyplace.com",
    description,
  };

  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "BulkBuyPlace",
    url: "https://bulkbuyplace.com",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://bulkbuyplace.com/?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://bulkbuyplace.com" },
    {
      "script:ld+json": organizationJsonLd,
    },
    {
      "script:ld+json": websiteJsonLd,
    },
  ];
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const { headers, supabase, session } = await getSupabaseWithSessionHeaders({
    request,
  });
  const requestUrl = new URL(request.url);
  const redirectTo = `${requestUrl.pathname}${requestUrl.search}`;
  const requiredTopStoreId = "bf7a5b47-0aaf-46e9-9420-2adbbc5ad78d";
  const defaultRatings = [4.9, 4.8, 4.8, 4.7, 4.7];

  let user: User | undefined;

  try {
    if (session) {
      const sessionUser = await getSessionUser({
        session,
        supabase,
      });
      const onboardingRedirect = redirectOnIncompleteOnBoarding({
        user: sessionUser,
        headers,
        redirectTo,
      });
      if (onboardingRedirect) {
        return onboardingRedirect.response;
      }
      user = sessionUser;
    }
  } catch (err) {
    console.log(err);
  }

  const { data: marketplaceStores, error: marketplaceStoresError } =
    await supabase
      .from(PUBLIC_STORE_PROFILES)
      .select("id, business_type");

  if (marketplaceStoresError) {
    console.error("Error fetching marketplace stores:", marketplaceStoresError);
  }

  const marketplaceStoreIds = new Set(
    (marketplaceStores ?? [])
      .filter((s) => isHomepageSupplierStore(s.business_type))
      .map((s) => s.id),
  );

  const isMarketplaceProduct = (p: { store_id: string }) =>
    marketplaceStoreIds.has(p.store_id);

  // Fetch trending/featured products (just get 5 active products for now)
  const { data: rpcProducts, error: productsError } = await supabase.rpc(
    "get_recently_sold_products",
    {
      p_limit: 5,
    },
  );

  if (productsError) {
    console.error("Error fetching products:", productsError);
  }

  let products = [...(rpcProducts ?? [])].filter(isMarketplaceProduct);

  if (products.length < 5) {
    const { data: allProducts, error: allProductsError } = await supabase.from(
      "Products",
    ).select("*").eq("status", "ACTIVE").limit(50);

    if (allProductsError) {
      console.error("Error fetching all products:", allProductsError);
    }

    if (allProducts?.length) {
      const seen = new Set(products.map((p: { id: string }) => p.id));
      for (const p of allProducts) {
        if (!isMarketplaceProduct(p)) continue;
        if (!seen.has(p.id)) {
          seen.add(p.id);
          products.push(p);
        }
        if (products.length >= 5) break;
      }
    }
  }

  // RPC or merged lists can contain the same product twice — dedupe before render
  {
    const seen = new Set<string>();
    products = products.filter((p: { id: string }) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }

  products = products.slice(0, 5);

  const { data: markdownProducts, error: markdownProductsError } =
    await supabase
      .from("Products")
      .select("*")
      .eq("status", "ACTIVE")
      .not("markdown", "is", null);

  if (markdownProductsError) {
    console.error("Error fetching markdown products:", markdownProductsError);
  }
  // Fetch active store names for the products
  const allProductStoreIds = [
    ...new Set([
      ...(products || []).map((p) => p.store_id),
      ...(markdownProducts || []).map((p) => p.store_id),
    ]),
  ];
  let storeMap: Record<string, string> = {};
  const activeStoreIds = new Set<string>();

  const storeBusinessTypeById = new Map<string, string | null>();

  if (allProductStoreIds.length > 0) {
    const { data: stores, error: storesError } = await supabase
      .from(PUBLIC_STORE_PROFILES)
      .select("id, name, business_type")
      .in("id", allProductStoreIds);

    if (storesError) {
      console.error("Error fetching stores:", storesError);
    } else if (stores) {
      for (const store of stores) {
        storeBusinessTypeById.set(store.id, store.business_type);
        if (!isHomepageSupplierStore(store.business_type)) continue;
        storeMap[store.id] = store.name;
        activeStoreIds.add(store.id);
      }
    }
  }

  // Only include products from active stores
  const productsWithStores = (products || [])
    .filter((product) => activeStoreIds.has(product.store_id))
    .map((product) => ({
      ...product,
      storeName: storeMap[product.store_id] || undefined,
    }));

  const activeMarkdownProducts = (markdownProducts || []).filter((p) =>
    activeStoreIds.has(p.store_id)
  );

  // Fetch top stores for homepage cards, always including requiredTopStoreId.
  const { data: requiredStore, error: requiredStoreError } = await supabase
    .from(PUBLIC_STORE_PROFILES)
    .select("id, name, slug, banner_pic, business_type")
    .eq("id", requiredTopStoreId)
    .single();

  if (requiredStoreError) {
    console.error("Error fetching required top store:", requiredStoreError);
  }

  const { data: otherTopStores, error: otherTopStoresError } = await supabase
    .from(PUBLIC_STORE_PROFILES)
    .select("id, name, slug, banner_pic, business_type")
    .neq("id", requiredTopStoreId)
    .limit(20);

  if (otherTopStoresError) {
    console.error("Error fetching additional top stores:", otherTopStoresError);
  }

  const topStores = [requiredStore, ...(otherTopStores || [])]
    .filter((
      store,
    ): store is {
      id: string;
      name: string;
      slug: string | null;
      banner_pic: string | null;
      business_type: string | null;
    } => Boolean(store) && isHomepageSupplierStore(store.business_type))
    .slice(0, 10)
    .map(({ business_type: _bt, ...store }) => store);

  return json(
    {
      userDetails: { ...user },
      trendingProducts: productsWithStores,
      topStores,
      markdownProducts: activeMarkdownProducts,
    },
    { headers },
  );
};

// Section Header Placeholder
export function SectionHeader(
  { title, seeAllHref }: { title: string; seeAllHref?: string },
) {
  return (
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      {seeAllHref && (
        <Link
          to={seeAllHref}
          className="group inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 text-sm font-semibold"
        >
          See All
          <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">
            →
          </span>
        </Link>
      )}
    </div>
  );
}

// Category Card Placeholder
// function CategoryCardPlaceholder({ name, icon }: { name: string; icon: string }) {
//   return (
//     <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-gray-300 transition-all cursor-pointer text-center">
//       <div className="text-4xl mb-3">{icon}</div>
//       <h4 className="font-medium text-gray-900">{name}</h4>
//     </div>
//   );
// }

// ============================================
// MAIN COMPONENT
// ============================================

export default function Index() {
  const { userDetails, trendingProducts, topStores, markdownProducts } =
    useLoaderData<typeof loader>();
  const { recentlyViewed } = useRecentlyViewed();
  const navigate = useNavigate();
  const [supplierZip, setSupplierZip] = useState("");
  const [supplierZipError, setSupplierZipError] = useState<string | null>(
    null,
  );

  const onSupplierZipChange = (value: string) => {
    setSupplierZipError(null);
    setSupplierZip(sanitizeUsZipInput(value));
  };

  const handleFindSuppliers = () => {
    const trimmed = supplierZip.trim();
    if (!trimmed) {
      setSupplierZipError(null);
      navigate("/suppliers");
      return;
    }
    if (!isValidUsZipCode(trimmed)) {
      setSupplierZipError(
        "Enter a valid US ZIP code (5 digits, or ZIP+4 as 12345-6789).",
      );
      return;
    }
    setSupplierZipError(null);
    navigate(`/suppliers?zip=${encodeURIComponent(trimmed)}`);
  };

  // const categories = [
  //   { name: "African Foods", icon: "🌍" },
  //   { name: "Asian Ingredients", icon: "🍜" },
  //   { name: "Caribbean", icon: "🌴" },
  //   { name: "Latin American", icon: "🌮" },
  //   { name: "Middle Eastern", icon: "🧆" },
  //   { name: "Spices", icon: "🌶️" },
  //   { name: "Grains & Rice", icon: "🍚" },
  //   { name: "Beverages", icon: "🥤" },
  // ];

  return (
    <div className="flex flex-col min-h-[100dvh] bg-gray-50">
      {/* Header */}
      <Header
        userId={userDetails.id || ""}
        userName={userDetails.first_name || ""}
        isAdmin={Boolean(userDetails.admin)}
      />

      {/* Main Content */}
      <main className="flex-1 pt-20">
        <section className="relative overflow-hidden bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 pb-10 pt-6 md:px-8 md:pb-16 md:pt-8">
            <div className="flex items-center gap-8">
              <div className="flex-1 max-w-xl">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight">
                  Find trusted international food suppliers near you
                </h1>
                <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                  Browse products, connect with verified suppliers, and place
                  orders for pickup or delivery.
                </p>

                {/* Narrow: stack, full width so input + button match. ~420px+: row, ZIP grows, button fixed. */}
                <div className="mt-6 flex w-full max-w-xl flex-col gap-2 min-[420px]:flex-row min-[420px]:items-stretch min-[420px]:gap-2">
                  <div className="flex min-w-0 flex-1 flex-col gap-1 min-[420px]:min-w-0">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      placeholder="Enter ZIP code"
                      value={supplierZip}
                      onChange={(e) => onSupplierZipChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleFindSuppliers();
                        }
                      }}
                      className="h-[42px] w-full min-w-0 rounded-lg border border-gray-300 px-4 text-sm focus:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      aria-invalid={supplierZipError ? "true" : undefined}
                      aria-describedby={supplierZipError
                        ? "hero-zip-error"
                        : undefined}
                    />
                    {supplierZipError && (
                      <p
                        id="hero-zip-error"
                        className="text-xs text-red-600"
                        role="alert"
                      >
                        {supplierZipError}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={handleFindSuppliers}
                    className="h-[42px] w-full shrink-0 whitespace-nowrap min-[420px]:w-auto min-[420px]:self-stretch min-[420px]:px-5"
                  >
                    Find Suppliers
                  </Button>
                </div>

                <div className="mt-4 flex items-center gap-3">
                  <Link
                    to="/sell"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors"
                  >
                    <Store className="h-4 w-4" />
                    Become a Supplier
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Verified suppliers
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Local delivery &amp; pickup
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    No middleman fees
                  </span>
                </div>
              </div>

              <div className="hidden md:block ml-auto">
                <Globe className="relative w-[280px]" />
              </div>
            </div>
          </div>
        </section>

        {/* ===== RECENTLY VIEWED SECTION ===== */}
        {recentlyViewed.length > 0 && (
          <section className="py-8 px-4 md:px-8">
            <div className="max-w-7xl mx-auto">
              <SectionHeader title="Recently Viewed" />
              <HorizontalScroller>
                {recentlyViewed.map((item) => (
                  <ProductCard
                    key={item.id}
                    productId={item.id}
                    productDetails={{
                      id: item.id,
                      name: item.name,
                      price: item.price,
                      markdown: item.markdown,
                      main_image: item.mainImageUrl,
                      media_urls: item.mainImageUrl
                        ? { "image.jpg": item.mainImageUrl }
                        : {},
                      store_id: item.storeId,
                    } as Product}
                    size="promo"
                    storeName={item.storeName || undefined}
                  />
                ))}
              </HorizontalScroller>
            </div>
          </section>
        )}

        {/* ===== FEATURED SUPPLIERS SECTION ===== */}
        <section className="py-8 px-4 md:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <SectionHeader title="Featured Suppliers" seeAllHref="/suppliers" />
            <HorizontalScroller>
              {topStores.map((store) => (
                <StoreCard
                  key={store.id}
                  name={store.name}
                  imageSrc={store.banner_pic}
                />
              ))}
            </HorizontalScroller>
          </div>
        </section>

        {/* ===== TRENDING PRODUCTS SECTION ===== */}
        {trendingProducts.length > 0 && (
          <section className="py-8 px-4 md:px-8">
            <div className="max-w-7xl mx-auto">
              <SectionHeader
                title="Trending Products"
                seeAllHref="/search?view=trending"
              />
              <HorizontalScroller>
                {trendingProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    productId={product.id}
                    productDetails={product as Product}
                    size="promo"
                    storeName={product.storeName}
                  />
                ))}
              </HorizontalScroller>
            </div>
          </section>
        )}

        {/* ===== SUPPLIER SPECIALS SECTION ===== */}
        {markdownProducts.length > 0 && (
          <section className="py-8 px-4 md:px-8 bg-white">
            <div className="max-w-7xl mx-auto">
              <SectionHeader
                title="Supplier Specials"
                seeAllHref="/search?view=specials"
              />
              <HorizontalScroller>
                {markdownProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    productId={product.id}
                    productDetails={product as Product}
                    size="promo"
                    storeName={product.storeName}
                  />
                ))}
              </HorizontalScroller>
            </div>
          </section>
        )}

        {/* ===== BROWSE BY CATEGORY SECTION ===== */}
        {
          /* <section className={`py-8 px-4 md:px-8 ${recentlyViewed.length > 0 ? "" : "bg-white"}`}>
          <div className="max-w-7xl mx-auto">
            <SectionHeader title="Browse by Category" seeAllHref="/categories" />
            <HorizontalScroller>
              {categories.map((cat, i) => (
                <CategoryCardPlaceholder key={i} {...cat} />
              ))}
            </HorizontalScroller>
          </div>
        </section> */
        }
      </main>

      <Footer />
    </div>
  );
}
